import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isReset = Boolean(token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const title = isReset ? "Set a new password" : "Reset your password";
  const subtitle = useMemo(
    () =>
      isReset
        ? "Choose a new password for your account"
        : "Enter your email and we’ll send reset instructions",
    [isReset]
  );

  const handleRequest = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setMessage("If that email is active, reset instructions have been sent.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Password reset is not available right now. Contact your administrator."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!password || password !== confirm) {
      setError("Passwords must match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setMessage("Password updated. You can sign in now.");
      setPassword("");
      setConfirm("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${BRAND.teal}15 0%, ${BRAND.purple}15 100%)`,
      }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
        <div
          className="px-8 pt-8 pb-6 text-center"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <img
            src={BRAND.logoUrl}
            alt="Wellform MD"
            className="h-24 w-24 mx-auto object-contain bg-white rounded-xl p-2 shadow-md"
            draggable="false"
          />
          <h1 className="mt-4 text-xl font-bold text-white tracking-wide">{title}</h1>
          <p className="mt-1 text-sm text-white/80">{subtitle}</p>
        </div>

        <form onSubmit={isReset ? handleReset : handleRequest} className="p-8 space-y-5">
          {isReset ? (
            <>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 transition"
                  style={{ "--tw-ring-color": BRAND.teal }}
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 transition"
                  style={{ "--tw-ring-color": BRAND.teal }}
                  disabled={loading}
                />
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@wellformmd.com"
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={loading}
              />
            </div>
          )}

          {message && (
            <div className="text-sm px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700">
              {message}
            </div>
          )}
          {error && (
            <div className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg text-white font-semibold shadow-sm transition disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2 justify-center">
                <Loader2 className="animate-spin" size={16} />
                Working…
              </span>
            ) : isReset ? (
              "Update password"
            ) : (
              "Send reset instructions"
            )}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
